import { useCallback } from "react";

import type { RightTopTab } from "@/sidecar/sidecarTabEventBus";

import ChatControlsBar from "@/components/chat/ChatControls/ChatControlsBar";
import { ChatPanelTimelineView } from "@/components/chat/ChatPanelTimelineView";
import { ChatQueue } from "@/components/chat/ChatQueue";
import { useChatWorkspaceData } from "@/components/chat/hooks/useChatWorkspaceData";
import { useLogger } from "@/sdk";
import { selectChatRightTopTab, useChatStore } from "@/sdk/agent/chat";
import { Sidecar } from "@/sidecar/Sidecar";

import { WorkspaceLayout } from "./redesigned-layout/WorkspaceLayout";

/**
 * Redesigned chat workspace content. Chat/session data is shared with the legacy `ChatManager` via
 * {@link useChatWorkspaceData} — change shared data needs there, not here, so the two don't drift.
 */
export function ChatWorkspaceContentRedesigned() {
    const logger = useLogger({ module: "ChatWorkspaceContentRedesigned", supportTeam: "messaging" });
    const {
        chatTasks,
        activeChatId,
        activeChatTask,
        onChatSelected,
        isSelectedChat,
        canShowChatDetails,
        shouldShowQueue,
        isSalesEnabled,
        isChatExpertAssistEnabled,
        areGuidesAvailable,
        guide,
        guideToolboxCommand,
        isGuideOpen,
        closeGuide,
    } = useChatWorkspaceData({ logger });

    // Per-chat right-top tab persistence (redesigned-only): restore the saved tab for the active chat
    // and write changes back, so switching between concurrent chats keeps each chat on its tab.
    const savedRightTopTab = useChatStore(selectChatRightTopTab(activeChatId));
    // The hook persists this chat's tab on mount, so a missing saved tab means the chat's first appearance.
    const isFirstAppearance = savedRightTopTab === undefined;
    const handleRightTopTabChange = useCallback((tab: RightTopTab) => {
        const id = useChatStore.getState().activeChatId;
        if (id) useChatStore.getState().setChatRightTopTab(id, tab);
    }, []);

    return (
        <WorkspaceLayout
            leftSidebar={
                <ChatQueue chats={chatTasks} isSelectedChat={isSelectedChat} onChatSelected={onChatSelected} />
            }
            leftSidebarOpen={shouldShowQueue}
            // Per product: the chat queue stays visible (experts juggle concurrent chats), unlike
            // voice's sidebar which hides on a call.
            leftSidebarHidden={false}
            leftTop={canShowChatDetails && activeChatTask ? <ChatControlsBar chatTask={activeChatTask} /> : undefined}
            leftTopOpen={canShowChatDetails}
            leftTopHeight="70px"
            leftBottom={<ChatPanelTimelineView activeChatTask={activeChatTask} />}
            rightTop={
                canShowChatDetails ? (
                    <Sidecar
                        // Remount per chat (do not remove): per-chat tab restore and the animateOnEnter
                        // freeze both seed at mount, so they only re-evaluate when this key changes.
                        // Fresh-mounting unit tests won't catch a regression here.
                        key={activeChatId}
                        customerSession={canShowChatDetails}
                        showAssistTab={isChatExpertAssistEnabled}
                        showOffersTab={isSalesEnabled}
                        initialTab={savedRightTopTab}
                        onTabChange={handleRightTopTabChange}
                        animateOnEnter={isFirstAppearance}
                        // open() (not the raw openGuide) so the tab keeps its error-toast fallback
                        // when no guide is available to launch.
                        openGuide={guideToolboxCommand.open}
                        closeGuide={closeGuide}
                        guide={guide}
                        areGuidesAvailable={areGuidesAvailable}
                        isGuideOpen={isGuideOpen}
                    />
                ) : null
            }
            rightTopOpen={canShowChatDetails}
        />
    );
}
